import {
  useSearchInput,
  useSearchKeyboard,
} from "src/utils/hooks/useSearchInput";
import styled, { css } from "styled-components";
import { A11yOutline } from "src/utils/accessibility";
import { spacing } from "src/design-system/tokens/spacing";
import { border_radius } from "src/design-system/tokens/border";
import { type InputHTMLAttributes, type ReactNode } from "react";
import type React from "react";
import color from "src/design-system/tokens/color";
import { tw } from "src/design-system/tw";
import { AutocompleteDropdown } from "../AutocompleteDropdown/AutocompleteDropdown";
import { ClickOutsideListener } from "../ClickAwayListener/ClickOutsideListener";

type SearchInputProps = {
  id: string;
  search: ReturnType<typeof useSearchInput>;
  keyboard: ReturnType<typeof useSearchKeyboard>;
  afterSelectEnter?: () => void;
  LeftElement?: ReactNode;
  RightElement?: ReactNode;
  className?: string;
  label?: string;
  as?: "li";
} & InputHTMLAttributes<HTMLInputElement>;
export function SearchInput({
  id,
  search,
  keyboard,
  afterSelectEnter,
  LeftElement,
  RightElement,
  className,
  label,
  as,
  ...props
}: Readonly<SearchInputProps>) {
  const showResults = !!search.displayResults.length;
  const ariaControlsID = `search-results-${id}`;

  function highlightText() {
    if (search.inputRef.current) {
      const textLength = search.inputRef.current.value.length;
      search.inputRef.current.setSelectionRange(0, textLength);
    }
  }

  function handleFocus() {
    keyboard.setFocusedElement({ id, index: 0 }, search.displayResults.length);
    highlightText();
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    keyboard.onKeyDown(
      event,
      search.handleEnter,
      search.isResultsClosed,
      search.displayResults.length,
      search.reset,
    );
  }

  function handleKeyUp(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter") {
      afterSelectEnter?.();
    }
  }

  return (
    <ClickOutsideListener as={as} onClickOutside={search.reset}>
      <div
        // Focus the input directly.
        tabIndex={-1}
        onClick={() => search.inputRef.current?.focus()}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            search.inputRef.current?.focus();
          }
        }}
        role="presentation"
        className={tw(
          "relative flex h-full flex-1 cursor-text items-center rounded-full bg-neutral-700 py-2 pr-0 pl-4 whitespace-nowrap hover:bg-neutral-800 lg:px-5",
          className,
        )}
      >
        {label && (
          <label
            id={`${id}-label`}
            className="w-full text-label-sm font-medium"
          >
            {label}
          </label>
        )}
        {LeftElement}
        <div
          className={tw(
            "relative flex w-full items-center overflow-hidden",
            LeftElement && "ml-2",
          )}
        >
          <span className="pointer-events-none absolute left-0 flex h-full w-full overflow-hidden text-label-lg leading-normal opacity-50">
            <span className="text-transparent">{search.displayValue}</span>
            <span>{search.hint}</span>
          </span>
          <input
            className="flex-1 border-0 bg-transparent text-body-lg leading-normal overflow-ellipsis outline-0 [&::-webkit-search-cancel-button]:hidden"
            ref={search.inputRef}
            type="search"
            value={search.displayValue}
            onChange={(e) => search.handleQueryChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onKeyUp={handleKeyUp}
            onFocus={handleFocus}
            aria-controls={ariaControlsID}
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showResults}
            aria-labelledby={`${id}-label`}
            {...props}
          />
        </div>
        {RightElement}
        {showResults && (
          <AutocompleteDropdown
            anchor="left"
            className="top-full w-full rounded-md bg-fill-primary shadow-md lg:w-90"
            results={search.autocompleteState.results}
            onSelectPlace={search.handleSelect}
            ariaControlsID={ariaControlsID}
          />
        )}
      </div>
    </ClickOutsideListener>
  );
}

/** @deprecated Replace this with tailwind */
export const InputBars = styled.div`
  display: flex;
  flex: 1;
  height: ${spacing.xxxxl};
`;

/** @deprecated Replace this with tailwind */
export const InputBar = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  flex: 1;
`;

/** @deprecated Replace this with tailwind */
export const BlockStyles = css`
  ${A11yOutline}
  display: flex;
  align-items: center;
  height: 100%;
  padding: ${spacing.md} ${spacing.lg};
  border-radius: ${border_radius.rounded_full};
  background-color: ${color.primitive.neutral700};
  &:hover {
    background-color: ${color.primitive.neutral800};
  }
`;

/** @deprecated Replace this with tailwind */
export const InputBlock = styled.div`
  ${BlockStyles}
  position: relative;
  gap: ${spacing.md};
  white-space: nowrap;
  flex: 1;
`;
